import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useWebSocketDeviceData } from '../../hooks/useWebSocketDeviceData'
import { useDataStore } from '../../store/dataStore'
import { useUnitPreference } from '../../contexts/UnitPreferenceContext'
import { formatPressureValue } from '../../utils/unitConversion'
import { OCAColors, Typography, Spacing } from '../../theme/colors'
import { DeviceCard } from '../ui/DeviceCard'
import { DeviceIcon } from '../ui/IOSIcons'
import { DeviceDetailPanel } from '../ui/DeviceDetailPanel'
import { StackedChevrons } from '../ui/StackedChevrons'
import { MaterialIcon } from '../ui/MaterialIcon'
import { DeviceRegistrationModal } from '../DeviceRegistrationModal'
import { buildLocationHierarchy } from '../../utils/locationHierarchy'
import { isDeviceOffline, formatTimeSinceLastSeen } from '../../utils/deviceStatus'
import type { Customer } from '../../types/locations'
import type { Device } from '../../services/api'

// Add spinner animation
const spinnerStyle = document.createElement('style')
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
if (!document.head.querySelector('[data-spinner-animation]')) {
  spinnerStyle.setAttribute('data-spinner-animation', 'true')
  document.head.appendChild(spinnerStyle)
}

// Zone Alarm States (matching frontend enum)
const enum ZoneAlarmState {
  NOT_ACTIVE = 0,
  NORMAL = 1,
  WARN = 2,
  ALARM = 3,
  ALARM_SILENCED = 4,
  ALARM_ACKNOWLEDGED = 5,
  NORMAL_PAST_UNACK_ALARM = 6,
  WARN_PAST_UNACK_ALARM = 7
}

interface PressureTrend {
  dp0?: { direction: 'up' | 'down'; count: number }
  dp1?: { direction: 'up' | 'down'; count: number }
  timestamp: number
}

interface PressureHistory {
  dp0: number[]
  dp1: number[]
}

export const DevicesPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { getAccessToken, isAuthenticated, user } = useAuth()
  const { pressureUnit } = useUnitPreference()

  // Get WebSocket data from the store (WebSocket is initialized in Layout component)
  const { deviceData: realtimeData } = useWebSocketDeviceData()

  // Global state from Zustand store
  const {
    devices,
    locations,
    deviceData,
    isLoadingDevices,
    isLoadingLocations,
    isLoadingDeviceData,
    devicesError,
    locationsError,
    deviceDataError,
    fetchAllData,
    fetchSingleDevice,
    cleanupExpiredNewDevices,
    isDeviceNew
  } = useDataStore()

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)
  const [registrationAccessToken, setRegistrationAccessToken] = useState<string>('')

  // Location filter from navigation state
  const [locationFilter, setLocationFilter] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)

  // Location hierarchy for manual filtering
  const [customers, setCustomers] = useState<Customer[]>([])
  const [showFilters, setShowFilters] = useState(false)


  // Pressure trend tracking
  const [pressureHistory, setPressureHistory] = useState<Map<string, PressureHistory>>(new Map())
  const [pressureTrends, setPressureTrends] = useState<Map<string, PressureTrend>>(new Map())

  // Pull-to-refresh state
  const [isPullRefreshing, setIsPullRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const touchStartY = useRef(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Check for location filter from navigation state
  useEffect(() => {
    const state = location.state as { locationFilter?: string, locationName?: string } | null
    if (state?.locationFilter) {
      console.log('📍 Setting location filter from navigation:', state.locationFilter, state.locationName)
      setLocationFilter(state.locationFilter)
      setLocationName(state.locationName || null)
    }
  }, [location.state])

  // Fetch all data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) return

      const token = await getAccessToken()
      if (!token) {
        console.error('Unable to get access token')
        return
      }

      // Fetch all data from store (uses caching to prevent 429 errors)
      await fetchAllData(token)
    }

    loadData()
  }, [isAuthenticated])

  // Build location hierarchy when locations change
  useEffect(() => {
    if (locations.length > 0) {
      const hierarchy = buildLocationHierarchy(locations)
      setCustomers(hierarchy.customers)
    }
  }, [locations])

  // Cleanup expired "new" device badges every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      cleanupExpiredNewDevices()
    }, 5000) // Check every 5 seconds

    return () => clearInterval(interval)
  }, [cleanupExpiredNewDevices])

  // Monitor WebSocket data for pressure trend changes
  useEffect(() => {
    realtimeData.forEach((deviceDataPoint, deviceId) => {
      if (deviceDataPoint.dp0 !== undefined || deviceDataPoint.dp1 !== undefined) {
        const currentDP0 = deviceDataPoint.dp0
        const currentDP1 = deviceDataPoint.dp1

        // Get current history for this device
        const currentHistory = pressureHistory.get(deviceId) || { dp0: [], dp1: [] }
        const newHistory = { ...currentHistory }

        // Calculate trends
        const trends: PressureTrend = { timestamp: Date.now() }

        // Helper function to calculate trend from history
        const calculateTrend = (values: number[], currentValue: number) => {
          if (values.length === 0) return null

          const allValues = [...values, currentValue]
          let consecutiveUp = 0
          let consecutiveDown = 0

          // Count consecutive movements from most recent backwards
          for (let i = allValues.length - 1; i > 0; i--) {
            const current = allValues[i]
            const previous = allValues[i - 1]

            if (current > previous) {
              if (consecutiveDown > 0) break // Different direction, stop counting
              consecutiveUp++
            } else if (current < previous) {
              if (consecutiveUp > 0) break // Different direction, stop counting
              consecutiveDown++
            } else {
              break // No change, stop counting
            }
          }

          if (consecutiveUp > 0) {
            return { direction: 'up' as const, count: Math.min(consecutiveUp, 3) }
          } else if (consecutiveDown > 0) {
            return { direction: 'down' as const, count: Math.min(consecutiveDown, 3) }
          }

          return null
        }

        // Update DP0 history and trend
        if (currentDP0 !== undefined) {
          newHistory.dp0 = [...newHistory.dp0, currentDP0].slice(-3) // Keep last 3 values
          const trend = calculateTrend(currentHistory.dp0, currentDP0)
          if (trend) {
            trends.dp0 = trend
          }
        }

        // Update DP1 history and trend
        if (currentDP1 !== undefined) {
          newHistory.dp1 = [...newHistory.dp1, currentDP1].slice(-3) // Keep last 3 values
          const trend = calculateTrend(currentHistory.dp1, currentDP1)
          if (trend) {
            trends.dp1 = trend
          }
        }

        // Update history
        setPressureHistory(prev => new Map(prev).set(deviceId, newHistory))

        // Update trends if any trend detected
        if (trends.dp0 || trends.dp1) {
          setPressureTrends(prev => new Map(prev).set(deviceId, trends))

          // Set timeout to clear trends after 30 seconds
          setTimeout(() => {
            setPressureTrends(prev => {
              const newMap = new Map(prev)
              const existing = newMap.get(deviceId)
              if (existing && existing.timestamp === trends.timestamp) {
                newMap.delete(deviceId)
              }
              return newMap
            })
          }, 30000)
        }
      }
    })
  }, [realtimeData])


  // Helper function to check if alarm is active (critical)
  const isAlarmActive = (state: number | null | undefined): boolean => {
    if (state === null || state === undefined) return false

    switch (state) {
      case ZoneAlarmState.ALARM:
      case ZoneAlarmState.ALARM_SILENCED:
      case ZoneAlarmState.ALARM_ACKNOWLEDGED:
        return true
      default:
        return false
    }
  }

  // Helper function to check if state is warning
  const isWarningState = (state: number | null | undefined): boolean => {
    if (state === null || state === undefined) return false

    switch (state) {
      case ZoneAlarmState.WARN:
      case ZoneAlarmState.WARN_PAST_UNACK_ALARM:
        return true
      default:
        return false
    }
  }

  // Pull-to-refresh handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isPullRefreshing) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || scrollContainer.scrollTop > 0) return

    const touchY = e.touches[0].clientY
    const distance = Math.max(0, touchY - touchStartY.current)

    if (distance > 0 && distance < 150) {
      setPullDistance(distance)
    }
  }

  const handleTouchEnd = async () => {
    if (pullDistance > 80 && !isPullRefreshing) {
      setIsPullRefreshing(true)
      setPullDistance(0)

      const token = await getAccessToken()
      if (token) {
        await fetchAllData(token, true) // Force refresh
      }

      setIsPullRefreshing(false)
    } else {
      setPullDistance(0)
    }
  }

  const getDeviceStatus = (device: Device): { status: string, color: string } => {
    const offline = isDeviceOffline(device.lastSeen)

    // Prioritize real-time WebSocket data
    const realtimeDeviceData = realtimeData.get(device.deviceId)
    if (realtimeDeviceData) {
      const dp0State = realtimeDeviceData.dp0_alarm_state
      const dp1State = realtimeDeviceData.dp1_alarm_state
      const hasAlarm = isAlarmActive(dp0State) || isAlarmActive(dp1State)

      // Check for active alarms that are also offline (red)
      if (hasAlarm && offline) {
        return { status: 'Alarm\n(Offline)', color: OCAColors.deviceAlarm }
      }

      // Check for active alarms (red)
      if (hasAlarm) {
        return { status: 'Alarm', color: OCAColors.deviceAlarm }
      }

      // Check for warnings (amber)
      if (isWarningState(dp0State) || isWarningState(dp1State)) {
        return { status: 'Warning', color: OCAColors.deviceWarning }
      }

      // All normal - green
      return { status: 'Online', color: OCAColors.deviceOnline }
    }

    // Fall back to API data
    const data = deviceData[device.deviceId]
    if (!data || data.length === 0) {
      return { status: 'No Data', color: OCAColors.deviceOffline }
    }

    const latestData = data[0]
    const dp0State = latestData.dp0_alarm_state
    const dp1State = latestData.dp1_alarm_state
    const hasAlarm = isAlarmActive(dp0State) || isAlarmActive(dp1State)

    // Check for active alarms that are also offline (red)
    if (hasAlarm && offline) {
      return { status: 'Alarm\n(Offline)', color: OCAColors.deviceAlarm }
    }

    // Check for active alarms (red)
    if (hasAlarm) {
      return { status: 'Alarm', color: OCAColors.deviceAlarm }
    }

    // Check for warnings (amber)
    if (isWarningState(dp0State) || isWarningState(dp1State)) {
      return { status: 'Warning', color: OCAColors.deviceWarning }
    }

    // Check last seen time for offline detection (> 5 minutes)
    if (offline) {
      return { status: 'Offline', color: OCAColors.deviceOffline }
    }

    // All normal - green
    return { status: 'Online', color: OCAColors.deviceOnline }
  }

  const getDeviceSubtitle = (device: Device): string | React.ReactNode => {
    const trends = pressureTrends.get(device.deviceId)

    // Prioritize real-time WebSocket data
    const realtimeDeviceData = realtimeData.get(device.deviceId)
    if (realtimeDeviceData) {
      const elements: React.ReactNode[] = []

      if (realtimeDeviceData.dp0 !== undefined && realtimeDeviceData.dp0 !== null) {
        const pressureText = `Zone 1: ${formatPressureValue(Number(realtimeDeviceData.dp0), pressureUnit)} ${pressureUnit}`
        if (trends?.dp0) {
          const trendColor = trends?.dp0?.direction === 'up' ? OCAColors.danger : OCAColors.success
          elements.push(
            <span key="dp0" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              {pressureText}
              <StackedChevrons
                direction={trends?.dp0?.direction || 'up'}
                count={trends?.dp0?.count as 1 | 2 | 3 || 1}
                color={trendColor}
                size={16}
              />
            </span>
          )
        } else {
          elements.push(<span key="dp0">{pressureText}</span>)
        }
      }

      if (realtimeDeviceData.dp1 !== undefined && realtimeDeviceData.dp1 !== null) {
        const dp1Text = `Zone 2: ${formatPressureValue(Number(realtimeDeviceData.dp1), pressureUnit)} ${pressureUnit}`
        if (trends?.dp1) {
          const trendColor = trends.dp1.direction === 'up' ? OCAColors.danger : OCAColors.success
          elements.push(
            <span key="dp1" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              {dp1Text}
              <StackedChevrons
                direction={trends.dp1.direction}
                count={trends.dp1.count as 1 | 2 | 3}
                color={trendColor}
                size={16}
              />
            </span>
          )
        } else {
          elements.push(<span key="dp1">{dp1Text}</span>)
        }
      }

      if (elements.length === 0 && realtimeDeviceData.timestamp) {
        return new Date(realtimeDeviceData.timestamp).toLocaleString()
      }

      if (elements.length > 0) {
        return (
          <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
            {elements.map((element, index) => (
              <span key={index} style={{ whiteSpace: 'nowrap' }}>{element}</span>
            ))}
          </span>
        )
      }

      return 'Real-time data'
    }

    // Fall back to API data
    const data = deviceData[device.deviceId]
    if (!data || data.length === 0) {
      return 'No recent data'
    }

    const latestData = data[0]
    const elements: React.ReactNode[] = []

    if (latestData.dp0 !== undefined && latestData.dp0 !== null) {
      elements.push(
        <span key="dp0">{`Zone 1: ${formatPressureValue(Number(latestData.dp0), pressureUnit)} ${pressureUnit}`}</span>
      )
    }

    if (latestData.dp1 !== undefined && latestData.dp1 !== null) {
      elements.push(
        <span key="dp1">{`Zone 2: ${formatPressureValue(Number(latestData.dp1), pressureUnit)} ${pressureUnit}`}</span>
      )
    }

    if (elements.length === 0) {
      return new Date(latestData.timestamp).toLocaleString()
    }

    if (elements.length > 0) {
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
          {elements.map((element, index) => (
            <span key={index} style={{ whiteSpace: 'nowrap' }}>{element}</span>
          ))}
        </span>
      )
    }

    return new Date(latestData.timestamp).toLocaleString()
  }

  const getDeviceValue = (device: Device): string => {
    return formatTimeSinceLastSeen(device.lastSeen)
  }

  const handleDeviceTap = (device: Device) => {
    setSelectedDevice(device)
    console.log('Opening device panel:', device.name || `Device ${device.deviceId?.slice(-5)}`)
  }

  const handleClosePanelProps = () => {
    setSelectedDevice(null)
  }

  const handleClearFilter = () => {
    console.log('📍 Clearing location filter')
    setLocationFilter(null)
    setLocationName(null)
    // Clear the navigation state by replacing with null state
    navigate('/devices', { replace: true, state: null })
  }

  const handleManualFilterSelect = (locationId: string, locationDisplayName: string) => {
    console.log('📍 Manually selecting location filter:', locationId, locationDisplayName)
    setLocationFilter(locationId)
    setLocationName(locationDisplayName)
    setShowFilters(false)
    // Update navigation state so filter persists on back/forward
    navigate('/devices', { replace: true, state: { locationFilter: locationId, locationName: locationDisplayName } })
  }

  // Show loading state while any data is loading
  const isLoading = isLoadingDevices || isLoadingLocations || isLoadingDeviceData
  const error = devicesError || locationsError || deviceDataError

  if (isLoading && devices.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: OCAColors.textSecondary,
        fontSize: Typography.fontSize.base,
      }}>
        Loading devices...
      </div>
    )
  }

  // Filter devices by location if filter is active
  const filteredDevices = locationFilter
    ? devices.filter((device: any) => device.locationId === locationFilter)
    : devices

  // Helper to sort devices: new devices first, then by lastSeen descending
  const sortDevices = (devices: Device[]) => {
    return devices.sort((a, b) => {
      const aIsNew = isDeviceNew(a.deviceId)
      const bIsNew = isDeviceNew(b.deviceId)

      // New devices come first
      if (aIsNew && !bIsNew) return -1
      if (!aIsNew && bIsNew) return 1

      // Within same "new" status, sort by lastSeen descending (most recent first)
      const aTime = new Date(a.lastSeen || 0).getTime()
      const bTime = new Date(b.lastSeen || 0).getTime()
      return bTime - aTime
    })
  }

  // Group devices by status and sort each group
  const onlineDevices = sortDevices(
    filteredDevices.filter((device: any) => {
      const status = getDeviceStatus(device)
      return status.status === 'Online'
    })
  )

  const warningDevices = sortDevices(
    filteredDevices.filter((device: any) => {
      const status = getDeviceStatus(device)
      return status.status === 'Warning'
    })
  )

  const alarmDevices = sortDevices(
    filteredDevices.filter((device: any) => {
      const status = getDeviceStatus(device)
      return status.status === 'Alarm' || status.status === 'Alarm\n(Offline)'
    })
  )

  const offlineDevices = sortDevices(
    filteredDevices.filter((device: any) => {
      const status = getDeviceStatus(device)
      return status.status === 'Offline' || status.status === 'No Data'
    })
  )

  return (
    <>
    <div
      ref={scrollContainerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        backgroundColor: '#F2F2F7',
        minHeight: '100vh',
        paddingBottom: '100px',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || isPullRefreshing) && (
        <div style={{
          position: 'absolute',
          top: pullDistance > 0 ? Math.min(pullDistance - 40, 40) : 10,
          left: '50%',
          transform: 'translateX(-50%)',
          transition: isPullRefreshing ? 'top 0.3s ease' : 'none',
          zIndex: 1000,
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            border: `3px solid ${OCAColors.gray300}`,
            borderTopColor: OCAColors.primary,
            borderRadius: '50%',
            animation: isPullRefreshing ? 'spin 1s linear infinite' : 'none',
          }} />
        </div>
      )}

      {/* iOS-style header */}
      <div style={{
        padding: Spacing.lg,
        backgroundColor: OCAColors.gray900,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        minHeight: '96px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: Spacing.md,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: Spacing.sm,
              marginBottom: Spacing.sm,
            }}>
              <img
                src="/OmniIQ_AppIcon_1024x1024.png"
                alt="OMNI IQ"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                }}
              />
              <h1 style={{
                fontSize: Typography.fontSize['2xl'],
                fontWeight: Typography.fontWeight.bold,
                color: OCAColors.background,
                margin: 0,
              }}>
                Devices
              </h1>
            </div>
            <div style={{
              fontSize: Typography.fontSize.sm,
              color: 'rgba(255, 255, 255, 0.7)',
            }}>
              {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''} {locationFilter ? 'in zone' : 'total'}
            </div>
          </div>

          {/* Filter and Clear buttons in header (icon-only) */}
          <div style={{ display: 'flex', gap: Spacing.xs, flexShrink: 0 }}>
            {locationFilter && (
              <button
                onClick={handleClearFilter}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: OCAColors.background,
                  border: 'none',
                  borderRadius: '8px',
                  padding: Spacing.sm,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                }}
              >
                <MaterialIcon name="close" size={20} color={OCAColors.background} />
              </button>
            )}

            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: OCAColors.background,
                border: 'none',
                borderRadius: '8px',
                padding: Spacing.sm,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
              }}
            >
              <MaterialIcon name="filter_alt" size={20} color={OCAColors.background} />
            </button>
          </div>
        </div>
      </div>

      {/* Location filter banner - shown when filter is active (display only) */}
      {locationFilter && locationName && (
        <div style={{
          backgroundColor: OCAColors.primary,
          padding: Spacing.md,
          display: 'flex',
          alignItems: 'center',
          gap: Spacing.sm,
        }}>
          <MaterialIcon name="filter_alt" size={20} color="white" />
          <span style={{
            color: 'white',
            fontSize: Typography.fontSize.sm,
          }}>
            Filtered to: <strong>{locationName}</strong>
          </span>
        </div>
      )}

      {/* Filter selection panel */}
      {showFilters && (
        <div style={{
          backgroundColor: OCAColors.background,
          borderBottom: `0.5px solid ${OCAColors.separator}`,
          maxHeight: '400px',
          overflowY: 'auto',
        }}>
          <div style={{
            padding: Spacing.md,
            borderBottom: `0.5px solid ${OCAColors.separator}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h3 style={{
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              color: OCAColors.label,
              margin: 0,
            }}>
              Select Location
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: Spacing.xs,
              }}
            >
              <MaterialIcon name="close" size={24} color={OCAColors.secondaryLabel} />
            </button>
          </div>

          {/* Hierarchical location list */}
          {customers.map((customer) => (
            <div key={customer.locationId}>
              {/* Customer */}
              <div
                onClick={() => handleManualFilterSelect(customer.locationId, customer.name || customer.locationId)}
                style={{
                  padding: Spacing.md,
                  paddingLeft: Spacing.lg,
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                  cursor: 'pointer',
                  backgroundColor: locationFilter === customer.locationId ? OCAColors.backgroundSecondary : OCAColors.background,
                }}
              >
                <div style={{
                  fontSize: Typography.fontSize.base,
                  fontWeight: Typography.fontWeight.medium,
                  color: OCAColors.label,
                }}>
                  {customer.name || customer.locationId}
                </div>
                <div style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.secondaryLabel,
                }}>
                  Customer
                </div>
              </div>

              {/* Sites */}
              {customer.sites?.map((site) => (
                <div
                  key={site.locationId}
                  onClick={() => handleManualFilterSelect(site.locationId, site.name || site.locationId)}
                  style={{
                    padding: Spacing.md,
                    paddingLeft: `calc(${Spacing.lg} + ${Spacing.lg})`,
                    borderBottom: `0.5px solid ${OCAColors.separator}`,
                    cursor: 'pointer',
                    backgroundColor: locationFilter === site.locationId ? OCAColors.backgroundSecondary : OCAColors.background,
                  }}
                >
                  <div style={{
                    fontSize: Typography.fontSize.base,
                    color: OCAColors.label,
                  }}>
                    {site.name || site.locationId}
                  </div>
                  <div style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                  }}>
                    Site
                  </div>
                </div>
              ))}

              {/* Projects under customer */}
              {customer.projects?.map((project) => (
                <div
                  key={project.locationId}
                  onClick={() => handleManualFilterSelect(project.locationId, project.name || project.locationId)}
                  style={{
                    padding: Spacing.md,
                    paddingLeft: `calc(${Spacing.lg} + ${Spacing.lg})`,
                    borderBottom: `0.5px solid ${OCAColors.separator}`,
                    cursor: 'pointer',
                    backgroundColor: locationFilter === project.locationId ? OCAColors.backgroundSecondary : OCAColors.background,
                  }}
                >
                  <div style={{
                    fontSize: Typography.fontSize.base,
                    color: OCAColors.label,
                  }}>
                    {project.name || project.locationId}
                  </div>
                  <div style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                  }}>
                    Project
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{
          padding: Spacing.md,
          margin: Spacing.md,
          backgroundColor: OCAColors.danger + '15',
          borderRadius: '8px',
          color: OCAColors.danger,
          fontSize: Typography.fontSize.sm,
        }}>
          {error}
        </div>
      )}

      {devices.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: Spacing.xl,
          color: OCAColors.textSecondary,
          fontSize: Typography.fontSize.base,
        }}>
          No devices found
        </div>
      ) : (
        <div style={{ padding: `${Spacing.md} ${Spacing.lg}` }}>
          {/* Alarm Devices Section */}
          {alarmDevices.length > 0 && (
            <div style={{ marginBottom: Spacing.lg }}>
              <h2 style={{
                fontSize: Typography.fontSize.xs,
                fontWeight: Typography.fontWeight.semibold,
                color: OCAColors.secondaryLabel,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: Spacing.sm,
                marginLeft: Spacing.xs,
              }}>
                ALARM
              </h2>
              {alarmDevices.map((device: any) => {
                const status = getDeviceStatus(device)
                return (
                  <DeviceCard
                    key={device.deviceId}
                    icon={<DeviceIcon backgroundColor={status.color} />}
                    title={device.name || `Device ${device.deviceId.slice(-5)}`}
                    subtitle={getDeviceSubtitle(device)}
                    value={getDeviceValue(device)}
                    status={status.status}
                    statusColor={status.color}
                    onClick={() => handleDeviceTap(device)}
                    isNew={isDeviceNew(device.deviceId)}
                  />
                )
              })}
            </div>
          )}

          {/* Warning Devices Section */}
          {warningDevices.length > 0 && (
            <div style={{ marginBottom: Spacing.lg }}>
              <h2 style={{
                fontSize: Typography.fontSize.xs,
                fontWeight: Typography.fontWeight.semibold,
                color: OCAColors.secondaryLabel,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: Spacing.sm,
                marginLeft: Spacing.xs,
              }}>
                WARNING
              </h2>
              {warningDevices.map((device: any) => {
                const status = getDeviceStatus(device)
                return (
                  <DeviceCard
                    key={device.deviceId}
                    icon={<DeviceIcon backgroundColor={status.color} />}
                    title={device.name || `Device ${device.deviceId.slice(-5)}`}
                    subtitle={getDeviceSubtitle(device)}
                    value={getDeviceValue(device)}
                    status={status.status}
                    statusColor={status.color}
                    onClick={() => handleDeviceTap(device)}
                    isNew={isDeviceNew(device.deviceId)}
                  />
                )
              })}
            </div>
          )}

          {/* Online Devices Section */}
          {onlineDevices.length > 0 && (
            <div style={{ marginBottom: Spacing.lg }}>
              <h2 style={{
                fontSize: Typography.fontSize.xs,
                fontWeight: Typography.fontWeight.semibold,
                color: OCAColors.secondaryLabel,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: Spacing.sm,
                marginLeft: Spacing.xs,
              }}>
                ONLINE
              </h2>
              {onlineDevices.map((device: any) => {
                const status = getDeviceStatus(device)
                return (
                  <DeviceCard
                    key={device.deviceId}
                    icon={<DeviceIcon backgroundColor={status.color} />}
                    title={device.name || `Device ${device.deviceId.slice(-5)}`}
                    subtitle={getDeviceSubtitle(device)}
                    value={getDeviceValue(device)}
                    status={status.status}
                    statusColor={status.color}
                    onClick={() => handleDeviceTap(device)}
                    isNew={isDeviceNew(device.deviceId)}
                  />
                )
              })}
            </div>
          )}

          {/* Offline Devices Section */}
          {offlineDevices.length > 0 && (
            <div style={{ marginBottom: Spacing.lg }}>
              <h2 style={{
                fontSize: Typography.fontSize.xs,
                fontWeight: Typography.fontWeight.semibold,
                color: OCAColors.secondaryLabel,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: Spacing.sm,
                marginLeft: Spacing.xs,
              }}>
                OFFLINE
              </h2>
              {offlineDevices.map((device) => {
                const status = getDeviceStatus(device)
                return (
                  <DeviceCard
                    key={device.deviceId}
                    icon={<DeviceIcon backgroundColor={status.color} />}
                    title={device.name || `Device ${device.deviceId.slice(-5)}`}
                    subtitle={getDeviceSubtitle(device)}
                    value={getDeviceValue(device)}
                    status={status.status}
                    statusColor={status.color}
                    onClick={() => handleDeviceTap(device)}
                    isNew={isDeviceNew(device.deviceId)}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}

    </div>

      {/* Device Registration Modal - Rendered via Portal to document.body */}
      {isAuthenticated && registrationAccessToken && user?.namespace?.id && ReactDOM.createPortal(
        <DeviceRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => setShowRegistrationModal(false)}
          accessToken={registrationAccessToken}
          namespaceId={user.namespace.id}
          onDeviceRegistered={async (deviceId: string) => {
            console.log('📱 Device registered, fetching single device:', deviceId)
            const token = await getAccessToken()
            if (token) {
              await fetchSingleDevice(deviceId, token)
            }
          }}
        />,
        document.body
      )}

      {/* Device Detail Panel - Rendered via Portal to document.body */}
      {ReactDOM.createPortal(
        <DeviceDetailPanel
          device={selectedDevice}
          isOpen={selectedDevice !== null}
          onClose={handleClosePanelProps}
          realtimeData={selectedDevice ? realtimeData.get(selectedDevice.deviceId) : undefined}
          pressureTrend={selectedDevice ? pressureTrends.get(selectedDevice.deviceId) : undefined}
        />,
        document.body
      )}

      {/* Floating Action Button - Rendered via Portal to document.body */}
      {ReactDOM.createPortal(
        <button
          onClick={async () => {
            const token = await getAccessToken()
            if (token) {
              setRegistrationAccessToken(token)
              setShowRegistrationModal(true)
            }
          }}
          style={{
            position: 'fixed',
            bottom: '104px',
            right: '20px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: OCAColors.primary,
            color: 'white',
            border: 'none',
            boxShadow: '0 8px 24px rgba(0, 122, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 998,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.92)'
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
          onTouchStart={(e) => {
            e.currentTarget.style.transform = 'scale(0.92)'
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
          onTouchCancel={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
          }}
        >
          <MaterialIcon name="add" size={28} color="white" />
        </button>,
        document.body
      )}
    </>
  )
}